// Adding a language: search by name or code, or type any valid code

import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { describeLanguage, normalizeLocale, suggestLocales } from "../../languages"

export function AddLanguage({ existing, onAdd, disabled }: {
  existing: string[]
  onAdd: (code: string) => void
  disabled?: boolean
}) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suggestions = useMemo(() => suggestLocales(query, existing), [query, existing])

  // What Enter would add: the typed code, if it's valid and new
  const typed = useMemo(() => {
    if (!query.trim()) return null
    const r = normalizeLocale(query)
    return r.code && !existing.some(e => e.toLowerCase() === r.code.toLowerCase()) ? describeLanguage(r.code) : null
  }, [query, existing])

  const add = (code: string) => {
    if (existing.some(e => e.toLowerCase() === code.toLowerCase())) {
      setError(`${describeLanguage(code).name} is already added.`)
      return
    }
    onAdd(code)
    setQuery("")
    setError(null)
    setOpen(false)
  }

  const submit = () => {
    const r = normalizeLocale(query)
    if (r.error) { setError(r.error); return }
    add(r.code)
  }

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          value={query}
          disabled={disabled}
          onChange={e => { setQuery(e.target.value); setOpen(true); setError(null) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => {
            if (e.key === "Enter") { e.preventDefault(); submit() }
            if (e.key === "Escape") setOpen(false)
          }}
          placeholder="Add a language the sites support: search by name or code"
          className="h-8 flex-1 text-xs"
        />
        <Button size="sm" className="h-8 gap-1 text-xs" disabled={disabled || !query.trim()} onClick={submit}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>
      {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
      {open && !disabled && (typed || suggestions.length > 0) && (
        <div className="absolute left-0 right-16 top-9 z-20 max-h-64 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {[...(typed && !suggestions.some(s => s.code === typed.code) ? [typed] : []), ...suggestions].map(l => (
            <button
              key={l.code}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => add(l.code)}
              className={cn("flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted")}
            >
              <span className="truncate">{l.name}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{l.code}</span>
              {l.direction === "rtl" && <span className="text-[9px] uppercase text-muted-foreground">rtl</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
